import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Radio, Checkbox, Col, Row } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { ArrowRight } from '../../../assets/icons';
import { updateUserProfile } from '../../../services/authApi/userApi';
import useGetUserProfile from '../../../Hooks/useGetUserProfile';
import { toastNotify } from '../../../utils/toastNotify';
import { addUserData, addCounter } from '../../../store/reducer/accountSlice';
import { useWatch } from 'antd/lib/form/Form';
import { preventReload } from '../../../utils/preventReload';
import StateSeeder from '../../../components/StateSeeder/StateSeeder';
import { getCachedStates } from '../../../utils/statesCache';
import { INVESTMENT_RANGE_OPTIONS } from '../../../utils/constant';

const StepFour = (props) => {
  const [loading, setLoaing] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [form] = Form.useForm();
  const { userProfileData } = useGetUserProfile();
  const preferredState1 = useWatch('preferredState1', form);
  const preferredState2 = useWatch('preferredState2', form);
  const preferredState3 = useWatch('preferredState3', form);
  const [preferredStateName1, setPreferredStateName1] = useState();
  const [preferredStateName2, setPreferredStateName2] = useState();
  const [preferredStateName3, setPreferredStateName3] = useState();
  const [statesByName, setStatesByName] = useState({});
  const dispatch = useDispatch();
  const hasCheckedRedirect = useRef(false);

  useEffect(() => {
    preventReload(form);
  }, []);

  // The backend stores investmentStates as an array of state names, not ids, but StateSeeder's
  // dropdown value is a stateId. Fetch the full states list once so we can resolve a saved
  // name back to its id when prefilling in edit mode. Uses the same cache as StateSeeder's own
  // fetch (below, one per state picker) so this page makes a single states request, not four.
  useEffect(() => {
    const fetchAllStates = async () => {
      try {
        const resp = await getCachedStates(1, 100);
        if (!resp.isError) {
          const map = {};
          resp?.data?.states?.forEach((s) => {
            map[s.name] = s.stateId;
          });
          setStatesByName(map);
        }
      } catch (error) {
        // Non-fatal — prefill just won't resolve saved state names to ids.
      }
    };
    fetchAllStates();
  }, []);

  // Check isWithdrawal and identificationDocs when component mounts
  useEffect(() => {
    if (userProfileData && Object.keys(userProfileData).length > 0 && !hasCheckedRedirect.current) {
      hasCheckedRedirect.current = true;

      // If investmentPreference is already set, the user already completed StepFour once
      // before (that's the only way StepSix's review/edit link for it exists) — don't
      // re-run the ID-required gate on an edit, only on the genuine first pass.
      if (userProfileData?.investmentPreference) {
        return;
      }

      const identificationDocs = userProfileData?.identificationDocs;
      const isDwollaVerified = userProfileData?.isDwollaVerified;

      // Redirect to UploadID only if isDwollaVerified is false AND identificationDocs is empty or null
      if (isDwollaVerified === false && (!identificationDocs || Object.keys(identificationDocs).length === 0)) {
        props.nextAction('UploadID');
      }
      // Otherwise show StepFour component (covers both other scenarios)
    }
  }, [userProfileData, props]);

  useEffect(() => {
    if (userProfileData?.investmentPreference && Object.keys(statesByName).length > 0) {
      const { investmentRange, investmentStates, isAgreed } = userProfileData.investmentPreference;
      const [name1, name2, name3] = investmentStates || [];

      form.setFieldsValue({
        investmentRange: investmentRange || undefined,
        preferredState1: statesByName[name1],
        preferredState2: statesByName[name2],
        preferredState3: statesByName[name3],
        isAgreed: isAgreed || false,
      });
      setPreferredStateName1(name1);
      setPreferredStateName2(name2);
      setPreferredStateName3(name3);

      handleFormChange();
    }
  }, [userProfileData, form, statesByName]);

  // Enable/disable the continue button
  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const hasState = values.preferredState1;
    const isComplete = values.investmentRange && hasState && values.isAgreed;
    setIsButtonDisabled(!isComplete);
  };

  const onStepFourSubmit = async (value) => {
    setLoaing(true);

    const investmentStates = [preferredStateName1, preferredStateName2, preferredStateName3].filter(Boolean);

    const stepFourFormData = {
      investmentPreference: {
        investmentRange: value.investmentRange,
        investmentStates,
        isAgreed: value.isAgreed
      },
    };

    try {
      const resp = await updateUserProfile(stepFourFormData);
      if (!resp.isError) {
        dispatch(addCounter(4));
        dispatch(addUserData({ userData: resp?.data?.user }));
        
        // After StepFour submission, always go to StepSix
        props.nextAction('StepSix');
      } else {
        toastNotify('error', resp?.message);
      }
    } catch (error) {
      toastNotify('error', error?.response?.data?.message);
    } finally {
      setLoaing(false);
    }
  };

  return (
    <>
      <Form
        className='auth-form stepfour investment-preferences'
        form={form}
        name='stepFourForm'
        onFinish={onStepFourSubmit}
        onValuesChange={handleFormChange}
      >
        <p>How much are you looking to invest in the next 12 months?</p>
        <Form.Item
          name='investmentRange'
          rules={[{ required: true, message: 'Please select an investment amount' }]}
        >
          <Radio.Group>
            <Row gutter={16}>
              {INVESTMENT_RANGE_OPTIONS.map((option) => (
                <Col span={24} key={option.value}>
                  <Radio value={option.value}>{option.label}</Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Form.Item>

        <p className='mb-16'>If you could invest in properties across the United States, which states would you be most interested in?</p>

        <p className='mb-8'>1st Choice</p>
        <StateSeeder
          value={preferredState1}
          setState={(value) => {
            form.setFieldValue('preferredState1', value);
            handleFormChange();
          }}
          setStateName={setPreferredStateName1}
          name='preferredState1'
          inputId='preferredState1'
          label='Select state'
          required={true}
        />
        <p className='mb-8'>2nd Choice</p>
        <StateSeeder
          value={preferredState2}
          setState={(value) => {
            form.setFieldValue('preferredState2', value);
            handleFormChange();
          }}
          setStateName={setPreferredStateName2}
          name='preferredState2'
          inputId='preferredState2'
          label='Select state'
          required={false}
        />
        <p className='mb-8'>3rd Choice</p>
        <StateSeeder
          value={preferredState3}
          setState={(value) => {
            form.setFieldValue('preferredState3', value);
            handleFormChange();
          }}
          setStateName={setPreferredStateName3}
          name='preferredState3'
          inputId='preferredState3'
          label='Select state'
          required={false}
        />

        <Form.Item
          name='isAgreed'
          valuePropName='checked'
          rules={[{
            validator: (_, value) =>
              value ? Promise.resolve() : Promise.reject(new Error('You must agree to continue'))
          }]}
        >
          <Checkbox>
            I agree to not invest more than 10% of my annual income or net worth – whichever is greater.
          </Checkbox>
        </Form.Item>

        <Form.Item className='auth-form__fixed-submit'>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            disabled={isButtonDisabled}
            className='ant-btn-primary--alt'
          >
            Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

StepFour.propTypes = {
  nextAction: PropTypes.func,
};

export default StepFour;
